import * as ClientsApi from 'api/clients-api';
import * as UserSessionApi from 'api/user-session-api';
import {useEffect, useState} from 'react';
import {getClientTier, isSkillsClient} from 'utils/authorization-utils';

import type {FetchClientResponse} from 'api/clients-api';
import type {UserProfileResponse} from 'api/user-session-api';
import type Logger from 'utils/logger';

export const useHeap = (
    window: Window & typeof globalThis,
    {
        logger,
        clientId,
        userId,
        locale,
    }: {
        logger: Logger;
        clientId: string;
        userId: string;
        locale: string;
    },
) => {
    const [client, setClient] = useState<FetchClientResponse>(null);
    const [userProfile, setUserProfile] = useState<UserProfileResponse>(null);

    useEffect(() => {
        if (!clientId) {
            return;
        }

        const fetchClient = async () => {
            try {
                const clientResponse = await ClientsApi.fetchClientAuthenticated();
                setClient(clientResponse);
            } catch (error) {
                logger.warn(`Failed to get client information. Heap.io will not be configured.`, error);
            }
        };

        fetchClient();

        return () => {
            setClient(null);
        };
    }, [clientId]);

    useEffect(() => {
        // disabling for clusters for now. We should instead call `GET /cluster/user-profile`
        if (!userId || !clientId) {
            return;
        }

        const fetchUserProfile = async () => {
            try {
                const userProfileResponse = await UserSessionApi.fetchUserProfile();
                setUserProfile(userProfileResponse);
            } catch (error) {
                logger.warn(`Failed to get user information. Heap.io will not be configured.`, error);
            }
        };

        fetchUserProfile();

        return () => {
            setUserProfile(null);
        };
    }, [userId]);

    useEffect(() => {
        if (!client || !userProfile || !locale || !window?.heap) {
            return;
        }

        identifyUser(window.heap, {logger, client, userProfile, locale});
    }, [client, userProfile, locale]);
};

/**
 *
 * Identifies the current user with Heap.io
 *
 * @param heap The global window.heap object
 * @param logger A logger object
 * @param client
 * @param user The parameters that identify the user
 * @param locale
 * @returns true if Heap was called successfully, false otherwise
 */
export const identifyUser = (
    heap: typeof window.heap,
    {
        logger,
        client,
        userProfile,
        locale,
    }: {
        logger: Logger;
        client: FetchClientResponse;
        userProfile: UserProfileResponse;
        locale: string;
    },
) => {
    if (heap) {
        const ClientIsSkillsClient = isSkillsClient();
        const ClientTier = getClientTier();
        const UserRole = userProfile.get('userGroups').get(0).get('userGroupType').get('ref');

        try {
            heap.identify(userProfile.get('userId'));
            heap.addUserProperties({
                ClientId: client.get('clientId'),
                ClientInstitutionName: client.get('institutionName'),
                ClientIsSkillsClient,
                ClientLocale: locale,
                ClientName: client.get('name'),
                ClientSubdomain: client.get('subDomain'),
                ClientTier,
                ClientVersion: __VERSION__,
                UserEmail: userProfile.get('email'),
                UserFirstName: userProfile.get('firstName'),
                UserLastName: userProfile.get('lastName'),
                UserRole,
                UserUsername: userProfile.get('userName'),
            });

            return true;
        } catch (err) {
            logger.error(`Failure when calling Heap.io: ${err.message}`);
            return false;
        }
    }

    return false;
};

/**
 *
 * Tracks a custom event with Heap.io.
 * Adds event properties if provided that are trackable in Heap.
 *
 * @param heap The global window.heap object
 * @param logger A logger object
 * @param event The name of the event to track - make this broad and the eventProperties specific.
 * @param properties Additional properties to send with the event in an object format with key-value pairs
 *
 * @returns true if Heap was called successfully, false otherwise
 */
export const track = (heap: typeof window.heap, logger: Logger, event: string, properties?: object) => {
    if (heap) {
        try {
            heap.track(event, {
                ...properties,
            });

            return true;
        } catch (err) {
            logger.error(`Failure when calling Heap.io: ${err.message}`);
            return false;
        }
    }

    return false;
};
